/**
 * ProfilePostCard Component
 * Unified card for displaying Events, Articles, and Opportunities
 * Used on: Profile pages, News page, Opportunities page, Home page
 * 
 * Props:
 * - post: The post object (event/article/opportunity)
 * - showRemoveButton: Boolean - show remove button for collaborated posts (own profile only)
 * - onRemove: Function - callback when remove button clicked
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDateShort, formatTime } from '../utils/dateFormatter';
import '../styles/colors.css';

const ProfilePostCard = ({ post, showRemoveButton = false, onRemove }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e) => {
    // Don't navigate if clicking remove button
    if (e.target.closest('.remove-button')) return;
    
    // Pass current location so detail page can navigate back here
    const returnPath = location.pathname;
    if (post.post_type === 'event') navigate(`/events/${post.id}`, { state: { from: returnPath } });
    else if (post.post_type === 'article') navigate(`/articles/${post.id}`, { state: { from: returnPath } });
    else if (post.post_type === 'opportunity') navigate(`/opportunities/${post.id}`, { state: { from: returnPath } });
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove) onRemove(post);
  };

  const truncate = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const renderContent = () => {
    if (post.post_type === 'event') {
      // Filter to show only bands
      const bands = post.collaborators?.filter(c => c.collaborator_role === 'band') || [];
      // Get venue name from collaborators
      const venue = post.collaborators?.find(c => c.collaborator_role === 'venue');
      const venueName = venue?.collaborator_name || null;
      
      return (
        <>
          {/* Event Image/Flyer */}
          <div style={{
            height: '60%',
            background: post.flyer_url 
              ? `url(${post.flyer_url}) center/cover` 
              : 'linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '14px',
            position: 'relative'
          }}>
            {!post.flyer_url && <span style={{ opacity: 0.5 }}>No Image</span>}
            
            {/* Age Restriction Badge */}
            {post.age_restriction && (
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                background: 'var(--accent-primary)',
                color: 'var(--text-dark)',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '700'
              }}>
                {post.age_restriction === 'all_ages' ? 'All Ages' : 
                 post.age_restriction === '18_plus' ? '18+' : '21+'}
              </div>
            )}
          </div>

          {/* Event Details */}
          <div style={{ padding: '14px', fontSize: '13px', lineHeight: '1.4', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Event Title */}
            <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '6px', color: 'var(--text-dark)', lineHeight: '1.3' }}>
              {post.title}
            </div>
            
            {/* Date, Time, Price */}
            <div style={{ fontWeight: '600', marginBottom: '6px', color: 'var(--text-dark)' }}>
              {formatDateShort(post.event_date)}
              {post.door_time && ` • ${formatTime(post.door_time)}`}
              {post.ticket_price !== undefined && post.ticket_price !== null && ` • $${post.ticket_price}`}
            </div>
            
            {/* Venue & Location */}
            <div style={{ color: 'var(--text-muted)', marginBottom: '6px', fontSize: '12px' }}>
              {venueName && `${venueName} • `}
              {post.city && post.state && `${post.city}, ${post.state}`}
            </div>
            
            {/* Band Names (Only bands) */}
            {bands.length > 0 && (
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: 'auto' }}>
                {bands.slice(0, 3).map((collab, idx) => (
                  <span key={idx} style={{
                    background: 'transparent',
                    border: '1px solid var(--accent-primary)',
                    color: 'var(--accent-primary)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    {collab.collaborator_name}
                  </span>
                ))}
                {bands.length > 3 && (
                  <span style={{
                    color: 'var(--text-muted)',
                    fontSize: '10px',
                    alignSelf: 'center'
                  }}>
                    +{bands.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>
        </>
      );
    }

    if (post.post_type === 'article') {
      return (
        <>
          {/* Article Cover Image */}
          <div style={{
            height: '60%',
            background: post.cover_photo_url 
              ? `url(${post.cover_photo_url}) center/cover` 
              : 'linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '14px'
          }}>
            {!post.cover_photo_url && <span style={{ opacity: 0.5 }}>No Image</span>}
          </div>

          {/* Article Details */}
          <div style={{ padding: '14px', fontSize: '13px', lineHeight: '1.4', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Title */}
            <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '6px', color: 'var(--text-dark)' }}>
              {truncate(post.description, 70)}
            </div>
            
            {/* Type & Date */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
              {post.article_type && post.article_type.length > 0 && (
                <span style={{
                  background: 'var(--smoke-gray)',
                  color: 'var(--text-dark)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {post.article_type[0].replace(/_/g, ' ')}
                </span>
              )}
              <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                {formatDateShort(post.created_at)}
              </span>
            </div>

            {/* Posted By */}
            {post.created_by_display_name && (
              <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '6px' }}>
                Posted by {post.created_by_display_name}
              </div>
            )}
            
            {/* Excerpt (body preview) */}
            {post.body && (
              <div style={{ 
                color: 'var(--text-muted)', 
                fontSize: '12px', 
                lineHeight: '1.5',
                marginTop: 'auto',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {truncate(post.body.replace(/<[^>]*>/g, ''), 120)}
              </div>
            )}
          </div>
        </>
      );
    }

    if (post.post_type === 'opportunity') {
      return (
        <>
          {/* Opportunity Title Display */}
          <div style={{
            height: '50%',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ color: 'var(--accent-primary)', fontSize: '18px', fontWeight: '700', lineHeight: '1.3' }}>
              {truncate(post.title, 80)}
            </div>
          </div>

          {/* Opportunity Details */}
          <div style={{ padding: '14px', fontSize: '13px', lineHeight: '1.4', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Date Range */}
            {(post.opportunity_start || post.opportunity_end) && (
              <div style={{ fontWeight: '600', marginBottom: '6px', color: 'var(--text-dark)' }}>
                {post.opportunity_start && formatDateShort(post.opportunity_start)}
                {post.opportunity_end && ` - ${formatDateShort(post.opportunity_end)}`}
              </div>
            )}
            
            {/* Location */}
            {(post.city || post.state || post.travel_distance) && (
              <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '6px' }}>
                {post.city && post.state && `${post.city}, ${post.state}`}
                {post.travel_distance && ` • ${post.travel_distance}mi radius`}
              </div>
            )}

            {/* Posted By + Time */}
            <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: 'auto' }}>
              Posted by {post.created_by_display_name || 'Unknown'} • {formatDateShort(post.created_at)}
            </div>
          </div>
        </>
      );
    }
  };

  const postTypeLabel = post.post_type === 'event' ? 'Event' : 
                         post.post_type === 'article' ? 'Article' : 
                         'Opportunity';

  return (
    <div 
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        aspectRatio: '1/1',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        background: 'var(--bg-card)',
        color: 'var(--text-dark)',
        borderRadius: '8px',
        border: isHovered ? '2px solid var(--accent-primary)' : '1px solid var(--border-light)',
        boxShadow: isHovered ? 'var(--shadow-hover)' : 'var(--shadow-card)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Post Type Badge */}
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        zIndex: 10,
        background: 'var(--accent-primary)',
        color: 'var(--text-dark)',
        padding: '4px 10px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {postTypeLabel}
      </div>

      {/* Remove Button (if shown) */}
      {showRemoveButton && (
        <button
          className="remove-button"
          onClick={handleRemove}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 10,
            background: 'var(--accent-danger)',
            color: 'var(--text-light)',
            border: 'none',
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#a50000'}
          onMouseLeave={(e) => e.target.style.background = 'var(--accent-danger)'}
        >
          Remove
        </button>
      )}

      {renderContent()}
    </div>
  );
};

export default ProfilePostCard;
